<script setup lang="ts">
import dayjs from 'dayjs'
import { BAIDU_MAP_AK, loadBaiduMapSdk } from '~/utils/surveyMapSdk'
const { t, locale } = useAppI18n()
const isChinese = computed(() => isChineseLocale(locale))
const chinesColon = computed(() => (isChinese.value ? '：' : ': '))
const { isRtl } = useRtl()

type SurveyTraderInfo = {
  localName?: string
  shortName?: string
  defaultName?: string
  localShortName?: string
  ico?: string
  logo?: string
  annotation?: string
  color?: string
  showName?: string
}

type SurveyMapItem = {
  baiduAddr?: string
  baiduZoom?: number
  baiduImg?: string
  googleImg?: string
  cover?: string
  images?: string[]
  mapIcon?: string
  country?: string
  surveyor?: string
  wiki_timestamp?: number | string
  evaluate?: string
  evaluatecolor?: string
  vurl?: string
  trader?: SurveyTraderInfo | null
  related?: SurveyTraderInfo | null
  [key: string]: unknown
}

type BaiduMapInstance = {
  centerAndZoom(point: unknown, zoom: number): void
  enableScrollWheelZoom(value: boolean): void
  disableDoubleClickZoom(): void
  addOverlay(overlay: unknown): void
  pointToOverlayPixel(point: unknown): {
    x: number
    y: number
  }
  panBy(x: number, y: number): void
  addEventListener(event: string, handler: () => void): void
  getPanes(): {
    labelPane: HTMLElement
    floatPane: HTMLElement
  }
}

type BaiduMarkerInstance = {
  addEventListener(event: string, handler: () => void): void
}

type BaiduMapNamespace = {
  Map: new (container: HTMLElement, options?: { enableMapClick?: boolean }) => BaiduMapInstance
  Overlay: new () => {
    initialize(map: BaiduMapInstance): HTMLElement
    draw(): void
  }
  Point: new (lng: number, lat: number) => unknown
  Marker: new (point: unknown, options?: unknown) => BaiduMarkerInstance
  Size: new (width: number, height: number) => unknown
  Icon: new (url: string, size: unknown, options?: { anchor?: unknown; imageSize?: unknown }) => unknown
}

type BaiduOverlayInstance = {
  initialize(map: BaiduMapInstance): HTMLElement
  draw(): void
}

type BaiduOverlayContext = BaiduOverlayInstance & {
  _map?: BaiduMapInstance
  _point?: unknown
  _element?: HTMLElement
}

type BaiduMapRuntime = {
  map: BaiduMapInstance
  point: unknown
  infoOverlay: BaiduOverlayInstance
}

/** 组件入参 */
const props = defineProps<{
  item?: SurveyMapItem | null
}>()

/** 地图根节点 */
const mapContainerRef = ref<HTMLDivElement | null>(null)

/** 信息卡根节点 */
const infoCardRef = ref<HTMLDivElement | null>(null)

/** 百度地图命名空间 */
const baiduNamespace = shallowRef<BaiduMapNamespace | null>(null)

/** 百度地图运行时实例 */
const mapRuntime = shallowRef<BaiduMapRuntime | null>(null)

/** 地图初始化失败状态 */
const mapLoadFailed = ref(false)

/** 信息卡显示状态 */
const showInfoCard = ref(true)

/** 是否正在恢复初始视角 */
const isRestoringInitialView = ref(false)

/** 信息窗底部箭头资源 */
const INFO_WINDOW_ARROW_ASSET =
  'https://resources1.interface003.com/web30/2026/06/15/8516c598-3b34-41aa-9324-a63c1a4a7027.png'

/** 信息窗位置图标资源 */
const INFO_WINDOW_LOCATION_ASSET =
  'https://resources1.interface003.com/web30/2026/06/11/c1e3e37f-9ec4-4e86-a4c1-7a5798d383f1.png'

/** 重置按钮资源 */
const RESET_BUTTON_ASSET =
  'https://resources1.interface003.com/web30/2026/06/15/7d09a919-c03e-49a1-b13f-b19879ecff8a.png'

const DEFAULT_MAP_MAP_ICON =
  'https://resources1.interface003.com/web30/2026/06/15/bc35e40f-b157-4567-acc3-b781f75b4b1a.gif'

const EXPO_MAP_ICON = 'https://resources1.interface003.com/web30/2026/06/23/d1f49e91-aef1-4ff2-8b4a-66ce68bd8d8a.gif'

const mapIcon = computed(() => (props.item?.isexhibition ? EXPO_MAP_ICON : DEFAULT_MAP_MAP_ICON))
/** 信息窗箭头宽度 */
const INFO_WINDOW_ARROW_WIDTH = 24

/** 信息窗箭头距离左侧的间距 */
const INFO_WINDOW_ARROW_LEFT_OFFSET = 24

/** 信息窗距离锚点顶部的偏移量 */
const INFO_WINDOW_OFFSET_Y = 40

/** 信息窗距离锚点左侧的偏移量 */
const INFO_WINDOW_OFFSET_X = INFO_WINDOW_ARROW_LEFT_OFFSET + INFO_WINDOW_ARROW_WIDTH / 2

/** 初始视图中 marker 的目标横向位置 */
const MAP_FOCUS_TARGET_X_RATIO = 0.5

/** 初始视图中信息卡距离地图顶部的目标间距 */
const INFO_WINDOW_TOP_MARGIN = 48

/** 初始视图中 marker 允许贴近底部的最小安全间距 */
const MAP_FOCUS_BOTTOM_MARGIN = 34

/** 地图业务数据 */
const mapItem = computed(() => props.item ?? {})

/** 交易商数据 */
const traderInfo = computed(() => mapItem.value.related || mapItem.value.trader || {})

/** 交易商名称 */
const traderName = computed(
  () =>
    traderInfo.value.showName ||
    traderInfo.value.defaultName ||
    traderInfo.value.localName ||
    traderInfo.value.shortName ||
    traderInfo.value.localShortName ||
    '--',
)

/** 交易商图标 */
const traderIcon = computed(() => traderInfo.value.ico || traderInfo.value.logo || '')

/** 实勘标签文案 */
const evaluateText = computed(() => mapItem.value.evaluate || traderInfo.value.annotation || '--')

/** 实勘标签颜色 */
const evaluateColor = computed(() => mapItem.value.evaluatecolor || traderInfo.value.color || '#1f845a')

/** VR 标签显隐 */
const showVrTag = computed(() => Boolean(mapItem.value.vurl))

/** 地图封面图 */
const mapCover = computed(
  () => mapItem.value.baiduImg || mapItem.value.googleImg || mapItem.value.cover || mapItem.value.images?.[0] || '',
)

/** 卡片封面图 */
const cardCover = computed(() => mapItem.value.cover || mapItem.value.images?.[0] || mapCover.value)

/**
 * 解析百度地图坐标字符串。
 * @param value `lng,lat` 格式的坐标字符串。
 * @returns 归一化后的经纬度对象，非法值回退为 0。
 */
function parseBaiduAddress(value?: string) {
  const segments = String(value || '').split(',')
  const lng = Number(segments[0])
  const lat = Number(segments[1])

  return {
    lng: Number.isFinite(lng) ? lng : 0,
    lat: Number.isFinite(lat) ? lat : 0,
  }
}

/** 百度经纬度 */
const baiduPoint = computed(() => parseBaiduAddress(mapItem.value.baiduAddr))

/** 是否存在有效坐标 */
const hasValidPoint = computed(() => Boolean(baiduPoint.value.lat || baiduPoint.value.lng))

/** 地图缩放级别 */
const baiduZoom = computed(() => (Number(mapItem.value.baiduZoom) > 0 ? Number(mapItem.value.baiduZoom) : 15))

/** 恢复初始视角解锁定时器 */
let restoreInitialViewTimer: number | null = null

/** 是否正在重建地图 */
let rebuildMapPromise: Promise<void> | null = null

/**
 * 初始化自定义信息窗覆盖物节点。
 * @param map 百度地图实例。
 * @returns 挂载后的信息窗 DOM 节点。
 */
function initializeInfoOverlay(this: BaiduOverlayContext, map: BaiduMapInstance) {
  /** 缓存地图实例 */
  this._map = map
  /** 复用 Vue 管理的卡片节点 */
  this._element!.style.position = 'absolute'
  this._element!.style.zIndex = '2'
  map.getPanes().floatPane.appendChild(this._element!)
  return this._element!
}

/**
 * 基于现有信息卡 DOM 创建百度地图覆盖物。
 * @returns 自定义覆盖物实例，缺少依赖时返回 `null`。
 */
function createInfoOverlay() {
  const BMap = baiduNamespace.value
  const infoCard = infoCardRef.value

  if (!BMap || !infoCard) {
    return null
  }

  /** 百度地图自定义覆盖物构造函数 */
  const OverlayConstructor = function (this: BaiduOverlayContext, point: unknown, element: HTMLElement) {
    /** 当前覆盖物锚点 */
    this._point = point
    /** 当前覆盖物节点 */
    this._element = element
  } as unknown as new (point: unknown, element: HTMLElement) => BaiduOverlayContext

  OverlayConstructor.prototype = new BMap.Overlay()

  OverlayConstructor.prototype.initialize = initializeInfoOverlay

  /** 根据点位重绘覆盖物 */
  OverlayConstructor.prototype.draw = function draw() {
    if (!this._map || !this._point || !this._element) {
      return
    }

    const pixel = this._map.pointToOverlayPixel(this._point)

    this._element.style.left = `${pixel.x - INFO_WINDOW_OFFSET_X}px`
    this._element.style.top = `${pixel.y - INFO_WINDOW_OFFSET_Y}px`
  }

  return new OverlayConstructor(
    mapRuntime.value?.point || new BMap.Point(baiduPoint.value.lng, baiduPoint.value.lat),
    infoCard,
  )
}

/** 同步信息窗覆盖物的屏幕坐标。 */
function syncOverlayPosition() {
  const runtime = mapRuntime.value

  if (!runtime) {
    return
  }
  runtime.infoOverlay.draw()
}

/** 调整地图初始焦点，保证信息窗完整显示。 */
function adjustMapFocus() {
  const runtime = mapRuntime.value
  const container = mapContainerRef.value

  if (!runtime || !container) {
    return
  }

  /** 当前锚点屏幕坐标 */
  const currentPixel = runtime.map.pointToOverlayPixel(runtime.point)

  /** 目标锚点横向位置 */
  const targetX = Math.round(container.clientWidth * MAP_FOCUS_TARGET_X_RATIO)

  /** 信息卡真实高度，包含底部箭头 */
  const infoCardHeight = infoCardRef.value?.offsetHeight || 0

  /** 目标锚点纵向位置
   * 让信息卡顶部稳定留出可视间距，而不是依赖固定比例。
   */
  const targetY = Math.min(
    Math.max(
      Math.round(infoCardHeight + INFO_WINDOW_OFFSET_Y + INFO_WINDOW_TOP_MARGIN),
      Math.round(container.clientHeight * 0.75),
    ),
    container.clientHeight - MAP_FOCUS_BOTTOM_MARGIN,
  )

  runtime.map.panBy(targetX - currentPixel.x, targetY - currentPixel.y)

  window.requestAnimationFrame(() => {
    syncOverlayPosition()
  })
}

/** 清理恢复初始视角状态和对应定时器。 */
function finishRestoreInitialView() {
  if (restoreInitialViewTimer) {
    window.clearTimeout(restoreInitialViewTimer)
    restoreInitialViewTimer = null
  }

  isRestoringInitialView.value = false
}

/** 重新创建地图实例并恢复到初始视角。 */
async function rebuildMapToInitialView() {
  if (rebuildMapPromise) {
    return await rebuildMapPromise
  }

  rebuildMapPromise = (async () => {
    if (mapContainerRef.value) {
      mapContainerRef.value.innerHTML = ''
    }

    mapRuntime.value = null
    showInfoCard.value = true

    await nextTick()
    createBaiduMap()
  })()

  try {
    await rebuildMapPromise
  } finally {
    rebuildMapPromise = null
  }
}

/** 创建百度地图实例、marker 和信息窗覆盖物。 */
function createBaiduMap() {
  const BMap = baiduNamespace.value

  if (!BMap || !mapContainerRef.value || !hasValidPoint.value) {
    return
  }

  /** 地图实例 */
  const map = new BMap.Map(mapContainerRef.value, {
    enableMapClick: false,
  })

  /** 经纬度点位 */
  const point = new BMap.Point(baiduPoint.value.lng, baiduPoint.value.lat)

  map.centerAndZoom(point, baiduZoom.value)
  map.enableScrollWheelZoom(true)
  map.disableDoubleClickZoom()

  /** 原生标记图标 */
  const nativeIcon = mapIcon.value
    ? new BMap.Icon(String(mapIcon.value), new BMap.Size(68, 68), {
        anchor: new BMap.Size(isRtl.value ? -33 : 40, 35),
      })
    : undefined

  /** 原生标记 */
  const marker = new BMap.Marker(point, {
    icon: nativeIcon,
  })

  /** 信息窗覆盖物 */
  const infoOverlay = createInfoOverlay()

  map.addOverlay(marker)
  if (infoOverlay) {
    map.addOverlay(infoOverlay)
  }

  if (!infoOverlay) {
    return
  }

  mapRuntime.value = {
    map,
    point,
    infoOverlay,
  }

  marker.addEventListener('click', () => {
    showInfoCard.value = !showInfoCard.value
  })

  syncInfoCardVisibility()
  adjustMapFocus()
}

/** 异步加载百度地图 SDK 并初始化地图。 */
async function initBaiduMap() {
  if (!hasValidPoint.value) {
    return
  }

  mapLoadFailed.value = false

  try {
    baiduNamespace.value = (await loadBaiduMapSdk(BAIDU_MAP_AK)) as BaiduMapNamespace
    createBaiduMap()
  } catch {
    mapLoadFailed.value = true
  }
}

/** 点击重置按钮后恢复地图默认视角。 */
async function restoreInitialMapView() {
  if (isRestoringInitialView.value) {
    return
  }

  isRestoringInitialView.value = true

  try {
    await rebuildMapToInitialView()
  } finally {
    restoreInitialViewTimer = window.setTimeout(() => {
      finishRestoreInitialView()
    }, 120)
  }
}

/** 在容器尺寸变化后刷新覆盖物定位。 */
function refreshOverlayPosition() {
  nextTick(() => {
    syncOverlayPosition()
  })
}

/** 将响应式显隐状态同步到信息卡 DOM。 */
function syncInfoCardVisibility() {
  const infoCard = infoCardRef.value

  if (!infoCard) {
    return
  }

  infoCard.style.display = showInfoCard.value ? 'block' : 'none'
}

onMounted(() => {
  initBaiduMap()
  window.addEventListener('resize', refreshOverlayPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', refreshOverlayPosition)
  finishRestoreInitialView()
  infoCardRef.value?.remove()
  mapRuntime.value = null
})

watch(
  () => props.item,
  async () => {
    await rebuildMapToInitialView()
    refreshOverlayPosition()
  },
  { deep: true },
)

watch(showInfoCard, () => {
  syncInfoCardVisibility()
  refreshOverlayPosition()
})
</script>

<template>
  <div class="relative w-full">
    <div class="relative aspect-[696/348] w-full overflow-hidden rounded-[12px] bg-[#eef3f7]">
      <div v-if="hasValidPoint && !mapLoadFailed" ref="mapContainerRef" class="absolute inset-0 h-full w-full" />

      <CommonImage
        v-else-if="mapCover"
        :src="mapCover"
        :alt="traderName"
        class="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />

      <button
        type="button"
        class="absolute top-[16px] right-[16px] z-[3] flex size-[32px] cursor-pointer items-center justify-center rounded-full hover:bg-[rgba(5,12,31,0.15)] rtl:right-auto rtl:left-[16px]"
        @click="restoreInitialMapView"
      >
        <CommonImage class="size-full" :src="RESET_BUTTON_ASSET" alt="restore initial map view" />
      </button>

      <div
        v-if="hasValidPoint && !mapLoadFailed"
        v-show="showInfoCard"
        ref="infoCardRef"
        class="absolute z-[2] w-[280px] -translate-y-full"
      >
        <div
          class="flex w-full flex-col items-center [filter:drop-shadow(0_0_0.5px_rgba(0,0,0,0.2))_drop-shadow(0_8px_10px_rgba(0,0,0,0.12))]"
        >
          <div class="flex w-full flex-col gap-[12px] rounded-[4px] bg-white p-[12px]">
            <div class="flex items-center justify-between gap-[12px]">
              <div class="flex min-w-0 items-center gap-[8px]">
                <CommonImage
                  v-if="traderIcon"
                  :src="traderIcon"
                  :alt="traderName"
                  class="size-[20px] shrink-0 rounded-[2px] object-cover"
                  loading="lazy"
                />
                <div class="truncate text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                  {{ traderName }}
                </div>
              </div>
            </div>

            <CommonImage
              v-if="cardCover"
              :src="cardCover"
              :alt="traderName"
              class="aspect-[1200/600] w-full rounded-[4px] object-cover"
              loading="lazy"
            />

            <div class="flex flex-col gap-[2px] rtl:items-start">
              <div class="flex w-full flex-wrap items-center gap-[4px]">
                <div
                  class="rounded-[2px] px-[8px] py-[2px] text-[11px] leading-[16px] text-white"
                  :style="{
                    backgroundColor: evaluateColor,
                  }"
                >
                  {{ evaluateText }}
                </div>
                <div
                  v-if="showVrTag"
                  class="rounded-[2px] bg-[#185ca0] px-[8px] py-[2px] text-[11px] leading-[16px] text-white"
                >
                  VR
                </div>
                <CommonImage
                  :src="INFO_WINDOW_LOCATION_ASSET"
                  alt=""
                  aria-hidden="true"
                  class="size-[14px] shrink-0"
                  loading="lazy"
                />
                <span class="text-[12px] leading-[18px] text-[#7d818a]">
                  {{ mapItem.country || '--' }}
                </span>
              </div>

              <div class="ftf text-[12px] leading-[18px] text-[#7d818a]">
                {{ t('84279') + chinesColon }}
                <span dir="ltr">{{ formatTimestamp(mapItem.wiki_timestamp) }}</span>
              </div>
              <div class="text-[12px] leading-[18px] text-[#7d818a]">
                {{ t('84280') + chinesColon }}
                <span>{{ mapItem.surveyor || '--' }}</span>
              </div>
            </div>
          </div>

          <div class="flex h-[6px] w-full items-center justify-start pl-[24px] rtl:justify-end">
            <CommonImage
              :src="INFO_WINDOW_ARROW_ASSET"
              alt=""
              aria-hidden="true"
              class="h-[6px] w-[24px]"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.anchorBL {
  display: none;
}
</style>
